import { Card, CardBody, Spinner } from "@heroui/react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ApiSdk } from "../../lib/api-sdk";
import { ResetPasswordForm } from "./ResetPasswordForm";
import { HashAlreadyUsed } from "./HashAlreadyUsed";
import { HashNotFound } from "./HashNotFound";
import { ValidationError } from "./ValidationError";
import { StatusMessage } from "./StatusMessage";

type PageState =
  | "no-hash"
  | "validating"
  | "valid"
  | "already-used"
  | "not-found"
  | "error";

export const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const hash = searchParams?.get("hash");
  const [pageState, setPageState] = useState<PageState>(
    hash ? "validating" : "no-hash"
  );

  const validateHash = async () => {
    if (!hash) return;
    setPageState("validating");

    try {
      const res = await ApiSdk.auth.validateHash({ requestBody: { hash } });
      setPageState(res?.valid ? "valid" : (res?.reason ?? "not-found"));
    } catch {
      setPageState("error");
    }
  };

  useEffect(() => {
    if (!hash) return;

    let cancelled = false;

    (async () => {
      try {
        const res = await ApiSdk.auth.validateHash({ requestBody: { hash } });
        if (!cancelled) setPageState(res?.valid ? "valid" : (res?.reason ?? "not-found"));
      } catch {
        if (!cancelled) setPageState("error");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hash]);

  return (
    <div className={"bg-primary"}>
      <div className={"flex justify-center align-middle items-center h-screen"}>
        <Card className={"md:p-8 py-8 w-full max-w-md mx-2"}>
          <CardBody>
            {pageState === "no-hash" && (
              <StatusMessage>
                The link seems to be broken or expired
              </StatusMessage>
            )}

            {pageState === "validating" && (
              <div className="flex justify-center py-8">
                <Spinner size="lg" />
              </div>
            )}

            {pageState === "valid" && <ResetPasswordForm hash={hash!} />}

            {pageState === "already-used" && <HashAlreadyUsed />}

            {pageState === "not-found" && <HashNotFound />}

            {pageState === "error" && (
              <ValidationError onRetry={validateHash} />
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
};
